import moment from 'moment';
import { SelectOption } from 'components/new-ui/composed/formSelect/FormSelect';
import { ContainerStatuses } from '../create-manifest-form/types';
import { UpdateManifestFormFields } from './update-manifest-validation.schema';

export type SelectOptionsMap = Partial<Record<keyof UpdateManifestFormFields, ReadonlyArray<SelectOption>>>;

export type AmendmentChange = {
    field: keyof UpdateManifestFormFields;
    label: string;
    currentValue: string;
    newValue: string;
};

export const fieldLabels: Record<keyof UpdateManifestFormFields, string> = {
    arrivalDateTime: 'Date and Time of Arrival',
    carrierOrHaulier: 'Carrier/Haulier',
    containerIndication: 'Container Indication',
    countryOfDispatch: 'Country of Dispatch',
    customsOfficeOfPresentation: 'Customs Office of Presentation',
    departureDateTime: 'Date and Time of Departure',
    meansId: 'Transport Means ID',
    meansIdentificationType: 'Transport Means ID Type',
    nationalityTransportMeans: 'Nationality Transport Means',
    referenceId: 'Manifest ID',
    vehicleRegistration: 'Vehicle Registration',
    departurePort: 'Departure Port',
    transportTypeId: 'Transport Type',
};

export const vehicleRegistrationLabels = {
    accompanied: 'RoRo IMO Number',
    unaccompanied: 'Trailer ID',
} as const;

const resolveLabel = (key: keyof UpdateManifestFormFields, values: UpdateManifestFormFields): string => {
    if (key === 'vehicleRegistration') {
        return values.containerIndication === ContainerStatuses.ACCOMPANIED
            ? vehicleRegistrationLabels.accompanied
            : vehicleRegistrationLabels.unaccompanied;
    }
    return fieldLabels[key];
};

const DATE_FIELDS: Array<keyof UpdateManifestFormFields> = ['arrivalDateTime', 'departureDateTime'];

const formatValue = (
    key: keyof UpdateManifestFormFields,
    values: UpdateManifestFormFields,
    selectOptionsMap: SelectOptionsMap
): string => {
    const value = values[key];

    if (value == null) return '-';

    if (key === 'containerIndication') {
        return value === ContainerStatuses.ACCOMPANIED ? 'Accompanied' : 'Unaccompanied';
    }

    if (DATE_FIELDS.includes(key)) {
        const m = moment(value as string);
        return m.isValid() ? m.format('YYYY-DD-MM, HH:mm') : String(value);
    }

    const options = selectOptionsMap[key];
    if (options) {
        const match = options.find((o) => String(o.id) === String(value));
        return match ? `${match.id} - ${match.value}` : String(value);
    }

    return String(value);
};

export const getChangedFields = (
    initialValues: UpdateManifestFormFields,
    values: UpdateManifestFormFields,
    selectOptionsMap: SelectOptionsMap
): AmendmentChange[] => {
    return (Object.keys(values) as Array<keyof UpdateManifestFormFields>)
        .filter((key) => {
            const initial = initialValues[key];
            const current = values[key];

            // Handle dates safely
            if (initial instanceof Date && current instanceof Date) {
                return initial.getTime() !== current.getTime();
            }

            return initial !== current;
        })
        .map((key) => ({
            field: key,
            label: resolveLabel(key, values),
            currentValue: formatValue(key, initialValues, selectOptionsMap),
            newValue: formatValue(key, values, selectOptionsMap),
        }));
};
